import { useEffect, useRef, useState } from 'react';
import { useDashboardProgramRunning } from '../../Components/Dashboard/LiveGraphStore';
import { useAppSelector } from '../../redux/ReduxHooks';
import { ApplicationActions } from '../../utils/constants';

const LIVE_ELAPSED_TICK_MS = 1000;

type RobotServiceWithStartTime = {
  startTime?: number | null;
};

function readRobotStartTime(): number | null {
  const startTime = (window.robotService as RobotServiceWithStartTime | undefined)?.startTime;
  if (typeof startTime === 'number' && Number.isFinite(startTime)) {
    return startTime;
  }

  return null;
}

function maxElapsedSecondsFromGraphs(graphs: Array<{ data: Array<{ time?: number }> }>): number {
  let max = 0;
  for (const graph of graphs) {
    const last = graph.data.at(-1);
    if (last && typeof last.time === 'number' && Number.isFinite(last.time)) {
      max = Math.max(max, last.time);
    }
  }
  return max;
}

function liveWholeSecondsFromStartTime(startTime: number): number {
  return Math.max(0, Math.floor((performance.now() - startTime) / LIVE_ELAPSED_TICK_MS));
}

function isFreshRunStartTime(startTime: number | null, previousStopStartTime: number | null): boolean {
  if (startTime == null) {
    return false;
  }

  if (previousStopStartTime == null) {
    return true;
  }

  return startTime !== previousStopStartTime;
}

/** Elapsed program time in seconds for live runs, or final duration in historic mode. */
export function useProgramElapsedSeconds(): number {
  const isProgramRunning = useDashboardProgramRunning();
  const dashboardViewMode = useAppSelector((state) => state.application.dashboardViewMode);
  const graphs = useAppSelector((state) => state.application.graphs);
  const isHistoric = dashboardViewMode === ApplicationActions.DashboardViewMode.HISTORIC;

  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const wasProgramRunningRef = useRef(false);
  const startTimeAtPreviousStopRef = useRef<number | null>(null);
  const awaitingFreshStartTimeRef = useRef(false);

  useEffect(() => {
    if (isHistoric) {
      setElapsedSeconds(maxElapsedSecondsFromGraphs(graphs));
      return;
    }

    const wasRunning = wasProgramRunningRef.current;
    wasProgramRunningRef.current = isProgramRunning;

    if (!isProgramRunning) {
      if (wasRunning) {
        startTimeAtPreviousStopRef.current = readRobotStartTime();
      }
      return;
    }

    if (!wasRunning) {
      awaitingFreshStartTimeRef.current = true;
      setElapsedSeconds(0);
    }

    const updateElapsed = () => {
      const startTime = readRobotStartTime();
      if (awaitingFreshStartTimeRef.current) {
        if (!isFreshRunStartTime(startTime, startTimeAtPreviousStopRef.current)) {
          setElapsedSeconds(0);
          return;
        }

        awaitingFreshStartTimeRef.current = false;
      }

      if (startTime == null) {
        setElapsedSeconds(0);
        return;
      }

      setElapsedSeconds(liveWholeSecondsFromStartTime(startTime));
    };

    updateElapsed();

    const intervalId = window.setInterval(updateElapsed, LIVE_ELAPSED_TICK_MS);
    return () => window.clearInterval(intervalId);
  }, [graphs, isHistoric, isProgramRunning]);

  return elapsedSeconds;
}
